import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ChevronRight, Loader2, MessageSquarePlus } from 'lucide-react';
import { toast } from 'sonner';
import { Layout } from '../components/Layout';
import { PageHeader } from '../components/PageHeader';
import { PageToolbar } from '../components/PageToolbar';
import { BackButton } from '../components/BackButton';
import FileDropzone from '../components/FileDropzone';
import { UnreadBadge } from '../components/UnreadBadge';
import { Conversation } from '../components/support/Conversation';
import { OnboardingTrack } from '../components/support/OnboardingTrack';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useAuthStore } from '../store/authStore';
import { useRegion } from '../hooks/useRegion';
import { cn } from '../lib/utils';
import {
  ATTACH_ACCEPT, currentPageName, CUSTOMER_KINDS, formatFileSize, formatMessageTime, statusClass, statusLabel, supportApi, toastApiError,
} from '../lib/support';

/**
 * Help: a customer's conversations with us. /help lists them, /help/:threadId
 * opens one. In the homepage demo the page is there, so the demo looks like
 * the real app, but explains that Help needs a real account.
 */
export default function HelpPage() {
  const { threadId } = useParams();
  const { isDemo } = useAuthStore();

  const [searchParams] = useSearchParams();
  const confirming = searchParams.get('onboarding') === 'paid';

  return (
    <Layout>
      <div className="space-y-8">
        {isDemo ? <DemoNotice />
          : threadId ? <HelpThread threadId={threadId} />
            : confirming ? <><PageHeader title="Help" /><OnboardingConfirming /></>
              : <HelpList />}
      </div>
    </Layout>
  );
}

function DemoNotice() {
  const navigate = useNavigate();
  const { logout } = useAuthStore();
  return (
    <>
      <PageHeader title="Help" />
      <div className="rounded-cards bg-surface p-6 sm:p-8 max-w-xl space-y-4" data-testid="help-demo-notice">
        <h2 className="section-title">Help isn&rsquo;t available in the demo</h2>
        <p className="text-muted-foreground">
          This demo is a throwaway account that disappears after an hour. Start a free trial and you
          can ask us anything from here, and we&rsquo;ll answer by email and in the app.
        </p>
        <Button
          onClick={async () => {
            await logout();
            navigate('/register');
          }}
        >
          Start a free trial
        </Button>
      </div>
    </>
  );
}

/**
 * Back from paying for onboarding. Polar's webhook opens the onboarding
 * conversation once the payment is confirmed, usually within seconds: wait
 * for it, then open it. Nothing says "booked" until it exists.
 */
function OnboardingConfirming() {
  const navigate = useNavigate();
  const { checkSubscriptionStatus } = useAuthStore();
  const [slow, setSlow] = useState(false);

  useEffect(() => {
    let stopped = false;
    let tries = 0;
    const look = async () => {
      if (stopped) return;
      tries += 1;
      try {
        const onboarding = await supportApi.myOnboarding();
        if (onboarding?.id) {
          checkSubscriptionStatus?.(true);
          toast.success('Payment received. Your onboarding is booked.');
          navigate(`/help/${onboarding.id}`, { replace: true });
          return;
        }
      } catch {
        // keep trying
      }
      if (tries === 15) setSlow(true);
      if (tries < 60) setTimeout(look, 2000);
    };
    look();
    return () => { stopped = true; };
  }, [navigate, checkSubscriptionStatus]);

  return (
    <div className="rounded-cards bg-surface p-6 sm:p-8 max-w-xl space-y-3" data-testid="onboarding-confirming">
      <h2 className="section-title flex items-center gap-3">
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" aria-hidden="true" />
        Confirming your payment
      </h2>
      <p className="text-muted-foreground">
        {slow
          ? 'This is taking longer than usual. If your payment went through, your onboarding will appear here shortly, and we\u2019ll email you. You can leave this page.'
          : 'One moment. As soon as it\u2019s confirmed, we\u2019ll open your onboarding conversation.'}
      </p>
    </div>
  );
}

function HelpList() {
  const navigate = useNavigate();
  const { region } = useRegion();
  const [threads, setThreads] = useState(null);
  const [tab, setTab] = useState('open');
  const [composing, setComposing] = useState(false);

  useEffect(() => {
    supportApi.myThreads().then(setThreads).catch((e) => {
      setThreads([]);
      toastApiError(e, 'Could not load your conversations');
    });
  }, []);

  // An onboarding in progress sits above the list, with where it's up to
  const onboarding = (threads || []).find((t) => t.kind === 'onboarding' && t.onboarding_stage !== 'done');
  // Unread replies under each tab, so one we answered and closed isn't missed
  const unread = { open: 0, closed: 0 };
  (threads || []).forEach((t) => {
    if (t.unread) unread[t.status === 'closed' ? 'closed' : 'open'] += 1;
  });
  const shown = (threads || [])
    .filter((t) => t !== onboarding)
    .filter((t) => (tab === 'closed' ? t.status === 'closed' : t.status !== 'closed'));

  return (
    <>
      <PageHeader title="Help" />
      <PageToolbar className="sm:flex-row sm:items-center sm:justify-between">
        <Tabs value={tab} onValueChange={setTab} className="w-full sm:w-auto">
          <TabsList>
            <TabsTrigger value="open" data-testid="help-tab-open">
              Open
              <UnreadBadge count={unread.open} label={`${unread.open} unread`} className="ml-2" />
            </TabsTrigger>
            <TabsTrigger value="closed" data-testid="help-tab-closed">
              Closed
              <UnreadBadge count={unread.closed} label={`${unread.closed} unread`} className="ml-2" />
            </TabsTrigger>
          </TabsList>
        </Tabs>
        <Button onClick={() => setComposing(true)} data-testid="help-new-request">
          <MessageSquarePlus />
          New request
        </Button>
      </PageToolbar>

      {onboarding && tab === 'open' && (
        <button
          type="button"
          onClick={() => navigate(`/help/${onboarding.id}`)}
          className="w-full text-left rounded-cards bg-surface hover:bg-mist transition-colors p-4 sm:p-5 space-y-3"
          data-testid="help-onboarding"
        >
          <span className="flex items-center justify-between gap-3">
            <span className="min-w-0">
              <span className="eyebrow block">onboarding</span>
              <span className={cn('block text-lg truncate', onboarding.unread && 'font-medium')}>{onboarding.subject}</span>
            </span>
            <span className="flex items-center gap-3 shrink-0">
              {onboarding.unread && <span className="w-2 h-2 rounded-full bg-brick" aria-label="New reply" />}
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
            </span>
          </span>
          <OnboardingTrack stage={onboarding.onboarding_stage} />
        </button>
      )}

      {threads === null ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
      ) : shown.length === 0 && !(onboarding && tab === 'open') ? (
        <div className="rounded-cards bg-surface p-6 sm:p-8 max-w-xl space-y-3" data-testid="help-empty">
          <h2 className="section-title">{tab === 'closed' ? 'Nothing closed yet' : 'How can we help?'}</h2>
          <p className="text-muted-foreground">
            {tab === 'closed'
              ? 'Conversations we’ve wrapped up land here. Reply to one and it opens again.'
              : 'Ask a question, tell us something isn’t working, or send us an idea. A person reads every one, and we’ll email you when we reply.'}
          </p>
          {tab !== 'closed' && <Button onClick={() => setComposing(true)}>New request</Button>}
        </div>
      ) : (
        <ul className="space-y-2" data-testid="help-threads">
          {shown.map((t) => (
            <li key={t.id}>
              <button
                type="button"
                onClick={() => navigate(`/help/${t.id}`)}
                className="w-full text-left rounded-cards bg-surface hover:bg-mist transition-colors p-4 sm:px-5 flex items-center gap-4 min-w-0"
              >
                <span
                  className={cn('w-2 h-2 rounded-full shrink-0', t.unread ? 'bg-brick' : 'bg-transparent')}
                  aria-label={t.unread ? 'New reply' : undefined}
                />
                <span className="flex-1 min-w-0">
                  <span className={cn('block truncate', t.unread && 'font-medium')}>{t.subject}</span>
                  <span className="block text-sm text-muted-foreground">
                    {t.kind_label} &middot; {formatMessageTime(t.last_message_at, region)}
                  </span>
                </span>
                <span className={cn('hidden sm:inline-flex text-xs px-2.5 py-1 rounded-pill whitespace-nowrap', statusClass(t.status))}>
                  {statusLabel(t.status, 'customer')}
                </span>
                <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <NewRequestDialog open={composing} onOpenChange={setComposing} />
    </>
  );
}

function NewRequestDialog({ open, onOpenChange }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [kind, setKind] = useState('question');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);

  const submit = async () => {
    if (!message.trim()) {
      toast.error('Write a message first');
      return;
    }
    setSending(true);
    try {
      const thread = await supportApi.start({ kind, subject, message, page: currentPageName(pathname) });
      toast.success('Sent. We’ll email you when we reply.');
      onOpenChange(false);
      setSubject('');
      setMessage('');
      setFiles([]);
      setKind('question');
      navigate(`/help/${thread.id}`);
    } catch (e) {
      toastApiError(e, 'Could not send your message');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>New request</DialogTitle>
          <DialogDescription>A person reads every one. We&rsquo;ll email you when we reply.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>What&rsquo;s it about?</Label>
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger data-testid="help-kind"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CUSTOMER_KINDS.map((k) => <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="help-subject">Subject <span className="text-muted-foreground font-normal">(optional)</span></Label>
            <Input id="help-subject" value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={120} data-testid="help-subject" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="help-message">Message</Label>
            <Textarea
              id="help-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={6}
              placeholder={kind === 'problem' ? 'What happened, and what did you expect?' : 'How can we help?'}
              data-testid="help-message"
            />
          </div>
          <div className="space-y-2">
            <Label>Files <span className="text-muted-foreground font-normal">(optional)</span></Label>
            <FileDropzone
              multiple
              compact
              accept={ATTACH_ACCEPT}
              title="Add screenshots or files"
              onSelect={(chosen) => setFiles((prev) => [...prev, ...chosen].slice(0, 10))}
            />
            {files.length > 0 && (
              <ul className="text-sm space-y-1">
                {files.map((f, i) => (
                  <li key={`${f.name}-${i}`} className="flex justify-between gap-3">
                    <span className="truncate">
                      {f.name} <span className="text-muted-foreground tabular-nums">{formatFileSize(f.size)}</span>
                    </span>
                    <button type="button" className="text-clay underline underline-offset-4 shrink-0"
                      onClick={() => setFiles((prev) => prev.filter((_, j) => j !== i))}>
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={sending || !message.trim()} data-testid="help-send">
            {sending && <Loader2 className="animate-spin" />}
            Send
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function HelpThread({ threadId }) {
  const navigate = useNavigate();
  const { region } = useRegion();
  const [thread, setThread] = useState(null);

  const load = useCallback(() => {
    supportApi.myThread(threadId).then(setThread).catch((e) => {
      toastApiError(e, 'Could not open that conversation');
      navigate('/help');
    });
  }, [threadId, navigate]);

  useEffect(() => { load(); }, [load]);

  const send = async ({ message, files }) => {
    try {
      setThread(files.length
        ? await supportApi.upload(threadId, files, message)
        : await supportApi.reply(threadId, message));
      return true;
    } catch (e) {
      toastApiError(e, 'Could not send your reply');
      return false;
    }
  };

  if (!thread) {
    return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;
  }

  return (
    <div className="max-w-3xl space-y-6">
      <div className="title-row gap-3 min-w-0">
        <BackButton onClick={() => navigate('/help')} label="Back to help" />
        <h1 className="record-title min-w-0 break-words">{thread.subject}</h1>
        <span className={cn('text-xs px-2.5 py-1 rounded-pill whitespace-nowrap', statusClass(thread.status))}>
          {statusLabel(thread.status, 'customer')}
        </span>
      </div>
      {thread.kind === 'onboarding' && <OnboardingTrack stage={thread.onboarding_stage} />}
      {thread.kind === 'onboarding' && thread.onboarding_stage === 'ready' && (
        <CompleteOnboarding onChange={setThread} />
      )}
      {thread.kind === 'onboarding' && thread.onboarding_stage !== 'done' && (
        <AccessNotice thread={thread} onChange={setThread} />
      )}
      <Conversation
        messages={thread.messages}
        mode="customer"
        region={region}
        onSend={send}
        getFileLink={supportApi.myFileLink}
        closed={thread.status === 'closed'}
      />
    </div>
  );
}


/**
 * Onboarding's account access, as the customer sees it: buying onboarding
 * lets us into their account to set up their data, and they can stop that.
 */
function AccessNotice({ thread, onChange }) {
  const [confirming, setConfirming] = useState(false);
  const [stopping, setStopping] = useState(false);

  const stop = async () => {
    setStopping(true);
    try {
      onChange(await supportApi.stopAccess());
      toast.success('Account access stopped');
      setConfirming(false);
    } catch (e) {
      toastApiError(e, 'Could not stop access');
    } finally {
      setStopping(false);
    }
  };

  if (!thread.account_access) {
    return (
      <p className="rounded-cards bg-mist px-4 py-3 text-sm text-muted-foreground" data-testid="access-stopped">
        Account access is stopped. We can&rsquo;t open your account; reply here if you&rsquo;d like us to carry on.
      </p>
    );
  }
  return (
    <div className="rounded-cards bg-mist px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="access-notice">
      <p className="flex-1 text-sm">
        As part of onboarding, we may access your account to set up your data. You&rsquo;ll see a note here
        each time we do.
      </p>
      {confirming ? (
        <div className="flex gap-2 shrink-0">
          <Button size="sm" variant="outline" onClick={() => setConfirming(false)} disabled={stopping}>Keep it</Button>
          <Button size="sm" variant="destructive" onClick={stop} disabled={stopping} data-testid="stop-access-confirm">
            {stopping && <Loader2 className="animate-spin" />}
            Stop access
          </Button>
        </div>
      ) : (
        <Button size="sm" variant="outline" className="shrink-0" onClick={() => setConfirming(true)} data-testid="stop-access">
          Stop access
        </Button>
      )}
    </div>
  );
}


/**
 * Once their gear is in, the customer says they're happy with it: onboarding
 * is done (closed, account access ended), and we're told.
 */
function CompleteOnboarding({ onChange }) {
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);

  const complete = async () => {
    setSaving(true);
    try {
      onChange(await supportApi.completeOnboarding());
      toast.success('Onboarding complete. Happy quoting!');
    } catch (e) {
      toastApiError(e, 'Could not complete onboarding');
      setSaving(false);
    }
  };

  return (
    <div className="rounded-cards bg-moss-soft text-moss px-4 py-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="complete-onboarding">
      <p className="flex-1 text-sm">
        {confirming
          ? 'This closes onboarding and ends our access to your account. You can still ask us anything in Help.'
          : 'Your gear is in. Had a look, and happy with it?'}
      </p>
      {confirming ? (
        <div className="flex gap-2 shrink-0">
          <Button size="sm" variant="outline" onClick={() => setConfirming(false)} disabled={saving}>Not yet</Button>
          <Button size="sm" onClick={complete} disabled={saving} data-testid="complete-onboarding-confirm">
            {saving && <Loader2 className="animate-spin" />}
            Yes, complete it
          </Button>
        </div>
      ) : (
        <Button size="sm" className="shrink-0" onClick={() => setConfirming(true)} data-testid="complete-onboarding-start">
          Onboarding complete
        </Button>
      )}
    </div>
  );
}
